import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LayoutComponent } from "../LayoutComponent";
import { useSelector } from "react-redux";
import {
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  Check2Circle,
  Plus,
  QuestionCircle,
  Trash,
} from "react-bootstrap-icons";
import {
  AlertIconFill,
  ArrowForwardIcon,
  ReplyAllIcon,
  ReplyIcon,
  SearchIcon,
  TrashIcon,
} from "../../Icons/IconsLib";
import FilterIcon from "@spectrum-icons/workflow/Filter";
import {
  Flex,
  Content,
  ProgressCircle,
  Button,
  View,
  Link,
  ListBox,
  IllustratedMessage,
  Heading,
  Item,
  ActionButton,
  DialogTrigger,
  DialogContainer,
  Dialog,
  MenuTrigger,
  Menu,
  TextField,
  DatePicker,
  Text,
  Checkbox,
  Divider,
  Form,
  Footer,
  Provider
} from "@adobe/react-spectrum";
import { ToastQueue } from '@adobe/react-spectrum';
import { CommentChatWindow } from "../../../../pages/protected/communication/component/message_list/CommentChatWindow";
import { EmailSenderContactBadge } from "../../messaging/EmailSenderContactBadge/EmailSenderContactBadge";
import NotFound from "@spectrum-icons/illustrations/NotFound";
import { RootState } from "../../../../state/store";
import moment from "moment";
import { useAsyncList } from "react-stately";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import { useTranslation } from "react-i18next";
import CardComponent from "./component/CardComponent";
import CardComponentLarge from "./component/CardComponentLarge";
import { BulkSelectionProvider } from "../BulkSelectCheckbox";
import { FPAData, FPADataTypes } from "../../../../infra/protected/FPA/FPAData";
import {
  getSenderEmail,
  getSenderName,
  truncateText,
} from "../../../../utils/helperUtil";
import {
  deriveUiMessageState,
  getDiscussionTargetMessageId,
  isCommentVisible,
  isDeleteVisible,
  isMarkDoneVisible,
  isResolveVisible,
  requiresDeleteConfirmation,
} from "../../../../utils/messageResolution";
import MessageAttachment from "./component/MessageAttachmentComponent";
import {
  invokeNewMessageCreating,
  invokeFinderApp,
  invokeReplyMessage,
} from "../../../../utils/NavigationUtils";
import {
  AtollonEvent,
  subscribe,
  unsubscribe,
} from "../../../../infra/events/Events";
import { useMessageOperations } from "../../../../hooks/useMessageOperations";
import { ResolveMessageOptionsDialog } from "../../../protected/Context/ResolveMessageOptionsDialog";
import { ContextMoveDialog } from "../../../protected/Context/ContextMoveDialog";
import "./message_list_component.css";
import styles from "./message_list_component.module.css";
import { GetProjectRequest } from "../../../../services/soap/project/requests/GetProjectRequest";
import { GetFolderRequest } from "../../../../services/soap/project/requests/GetFolderRequest";

/**
 * Helper function to extract recipient value from various data formats
 * Handles: arrays (of strings or objects), strings, and objects with CONTENT property
 */
const getRecipientValue = (data: any): string | null => {
  if (!data) return null;

  if (Array.isArray(data) && data.length > 0) {
    const values = data
      .map((item) => {
        if (typeof item === "string") {
          return item.trim();
        }
        if (item?.CONTENT && typeof item.CONTENT === "string") {
          return item.CONTENT.trim();
        }
        const alt =
          (typeof item?.contact === "string" && item.contact.trim()
            ? item.contact
            : undefined) ??
          (typeof item?.contactName === "string" && item.contactName.trim()
            ? item.contactName
            : undefined) ??
          (typeof item?.userName === "string" && item.userName.trim()
            ? item.userName
            : undefined) ??
          (typeof item?.name === "string" && item.name.trim()
            ? item.name
            : undefined) ??
          (typeof item?.emailText === "string" && item.emailText.trim()
            ? item.emailText
            : undefined) ??
          (typeof item?.id === "string" && item.id.trim()
            ? item.id
            : undefined);
        if (alt) return String(alt).trim();
        return null;
      })
      .filter(Boolean);

    return values.length > 0 ? values.join(", ") : null;
  }
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data.CONTENT && typeof data.CONTENT === "string" && data.CONTENT.trim()) {
    return data.CONTENT.trim();
  }
  if (data && typeof data === "object") {
    const alt =
      (typeof data.contact === "string" && data.contact.trim()
        ? data.contact
        : undefined) ??
      (typeof data.contactName === "string" && data.contactName.trim()
        ? data.contactName
        : undefined) ??
      (typeof data.userName === "string" && data.userName.trim()
        ? data.userName
        : undefined) ??
      (typeof data.name === "string" && data.name.trim()
        ? data.name
        : undefined) ??
      (typeof data.emailText === "string" && data.emailText.trim()
        ? data.emailText
        : undefined) ??
      (typeof data.id === "string" && data.id.trim() ? data.id : undefined);
    if (alt) return String(alt).trim();
  }
  return null;
};

/**
 * Component to render the "To" and "Cc" recipient fields
 * Only renders fields that have valid data
 */
const RecipientToField = ({ recipient }: { recipient: any }) => {
  const { t } = useTranslation();
  const toValue = getRecipientValue(recipient?.TO);
  const ccValue = getRecipientValue(recipient?.CC);

  // Don't render anything if neither To nor Cc has data
  if (!toValue && !ccValue) return null;

  return (
    <Flex
      direction="row"
      justifyContent="start"
      alignItems="center"
      width="100%"
      gap="size-200"
      UNSAFE_className={styles.email_to}
    >
      {toValue && (
        <View>
          <span title={toValue} style={{ color: "#728289" }}>
            {t("to", { ns: "layout_components", defaultValue: "To" })}
          </span>
          &nbsp;{truncateText(toValue, 150)}
        </View>
      )}
      {ccValue && (
        <View>
          <span title={ccValue} style={{ color: "#728289" }}>
            {t("cc", { ns: "layout_components", defaultValue: "Cc" })}
          </span>
          &nbsp;{truncateText(ccValue, 150)}
        </View>
      )}
    </Flex>
  );
};

const RecipientNotifyField = ({ recipient }: { recipient: any }) => {
  const notifyValue = getRecipientValue(recipient?.USER ?? recipient?.NOTIFY);
  if (!notifyValue) return null;

  return (
    <Flex
      direction="row"
      justifyContent="start"
      alignItems="center"
      width="100%"
      gap="8px"
      wrap
      UNSAFE_className={styles.notify_recipient_row}
    >
      <View>
        {truncateText(notifyValue, 150)}
        </View>

    </Flex>
  );
};

const ContextPathLinks = ({
  message,
  styles,
}: {
  message?: any;
  styles: Record<string, string>;
}) => {
  // Activity navigation: prefer the actual numeric activity node id (`activityId`,
  // matching how `SearchUtils.processHistoryData` sets the activity context id).
  // `activityRefId` is the activity's display reference code (e.g. used as a
  // breadcrumb subtitle in search results) and may be non-numeric — feeding it
  // to the Finder app makes `+id` resolve to NaN and `GetContextPath` errors out
  // after the app opens. We only fall back to it when it is itself numeric.
  const resolveActivityId = (msg: any): string => {
    const candidates = [msg?.activityId, msg?.activityRefId];
    for (const candidate of candidates) {
      const value = String(candidate ?? "").trim();
      if (value && value !== "0" && /^\d+$/.test(value)) {
        return value;
      }
    }
    return "";
  };

  const contextItems = [
    {
      key: "folder",
      id: String(message?.folderId || "").trim(),
      name: message?.folderName,
      contextType: "folder",
    },
    {
      key: "project",
      id: String(message?.projectId || "").trim(),
      name: message?.projectName,
      contextType: "project",
    },
    {
      key: "activity",
      id: resolveActivityId(message),
      name: message?.activityName,
      contextType: "activity",
    },
  ].filter((item) => item.name);

  if (contextItems.length === 0) return null;

  // Only render as a clickable link when the resolved id is a positive integer
  // node id; otherwise fall back to plain text so the Finder cannot be invoked
  // with an invalid contextId (which previously caused activity-level errors).
  const isLinkableId = (id: string): boolean =>
    !!id && id !== "0" && /^\d+$/.test(id);

  return (
    <Flex
      direction="row"
      gap="8px"
      alignItems="center"
      wrap
      UNSAFE_className={styles.context_path_row}
    >
      {contextItems.map((item, index) => (
        <Fragment key={item.key}>
          {index > 0 && (
            <View UNSAFE_className={styles.icon_chevron_right}>{">"}</View>
          )}
          {isLinkableId(item.id) ? (
            <span
              className={styles.context_path_link}
              onClick={() => invokeFinderApp(item.id, item.contextType)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  invokeFinderApp(item.id, item.contextType);
                }
              }}
              role="link"
              tabIndex={0}
            >
              {item.name}
            </span>
          ) : (
            <View UNSAFE_className={styles.address}>{item.name}</View>
          )}
        </Fragment>
      ))}
    </Flex>
  );
};

interface MessageListProps {
  readonly selectedItem?: FPAData;
}
export interface Character {
  id: string;
}

/**
 * Centered placeholder shown inside the message-detail pane while two or
 * more rows are bulk-selected. Mirrors the Communication app's
 * `BulkSelectionPlaceholder` so context-level bulk actions stay in parity.
 */
const BulkSelectionPlaceholder = ({
  count,
  label,
  stylesMap,
}: {
  count: number;
  label: string;
  stylesMap: Record<string, string>;
}) => (
  <Flex
    direction="column"
    alignItems="center"
    justifyContent="center"
    width="100%"
    height="100%"
    UNSAFE_className={stylesMap.bulk_selection_placeholder}
  >
    <View UNSAFE_className={stylesMap.bulk_selection_placeholder_count}>
      {count}
    </View>
    <View UNSAFE_className={stylesMap.bulk_selection_placeholder_label}>
      {label}
    </View>
  </Flex>
);

function MessageList({ selectedItem }: MessageListProps) {
  const { t } = useTranslation();
  const componentRef = useRef<HTMLDivElement>(null);
  const { store, messageService, mainService, projectService } = useDependency();
  const selectedApp = useSelector(
    (state: RootState) => state.userWebLayout.selectedAppId,
  );
  const { isMobile } = useViewInfo();

  useEffect(() => {
    // Check if there's a pending reload from a message that was sent while component was unmounted
    const needsReload = sessionStorage.getItem("messageListNeedsReload");
    const reloadContext = sessionStorage.getItem("messageListReloadContext");

    if (needsReload === "true") {
      // Clear the flag
      sessionStorage.removeItem("messageListNeedsReload");
      sessionStorage.removeItem("messageListReloadContext");

      // Check if this is the right context
      if (!reloadContext || reloadContext === String(selectedItem?.id)) {
        setReloadTrigger((prev) => prev + 1);
      }
    }
  }, [selectedItem?.id]);

  const [rowsData, setRowsData] = useState<any>([]);
  const [messageContent, setMessageContent] = useState<any>("");
  const [selectedKeys, setSelectedKeys] = useState<Set<any>>();
  const [selectedDataRow, setSelectedDataRow] = useState<any>({});
  const [navigationIndex, setNavigationIndex] = useState<number>(0);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [showLoaderDetail, setShowLoaderDetail] = useState(true);
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [stopLoadData, setStopLoadData] = useState<boolean>(false);
  const [totalMessageListRec, setTotalMessageListRec] = useState<number>(0);
  const [emailAttachments, setEmailAttachments] = useState<any>([]);
  const [reloadTrigger, setReloadTrigger] = useState<number>(0); // Add reload trigger
  const [isCommentChatOpen, setIsCommentChatOpen] = useState<boolean>(false);
  const [commentChatTarget, setCommentChatTarget] = useState<any>(null);
  const [isResolveOptionsOpen, setIsResolveOptionsOpen] = useState<boolean>(false);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState<boolean>(false);
  const [bulkSelectedKeys, setBulkSelectedKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const uiMessageState = useMemo(
    () =>
      deriveUiMessageState({
        selectedMessage: selectedDataRow,
        detailMessage: messageContent?.MESSAGE,
        forceContextStorage: true,
      }),
    [messageContent?.MESSAGE, selectedDataRow],
  );
  const showCommentAction = isCommentVisible(uiMessageState);
  const showDeleteAction = isDeleteVisible(uiMessageState);
  const showMarkDoneAction = isMarkDoneVisible(uiMessageState);
  const showMoveAction = true;
  const showResolveAction = isResolveVisible(uiMessageState);
  const shouldConfirmDelete = requiresDeleteConfirmation(uiMessageState);
  const showReplyAction = uiMessageState.supportsReply;
  const showForwardAction = uiMessageState.supportsForward;
  const hasBulkSelection = bulkSelectedKeys.size > 0;
  const bulkSelectedCount = bulkSelectedKeys.size;
  // Apple-Mail parity with the Communication app: 2+ ticks collapses the
  // detail pane to a numeric placeholder. A single tick still honors the
  // previously opened detail so single-select flows are unchanged.
  const isBulkDetailSuppressed = bulkSelectedCount >= 2;
  const bulkSelectedRows = useMemo(
    () =>
      rowsData.filter((row: any) =>
        bulkSelectedKeys.has(String(row?.id ?? "")),
      ),
    [bulkSelectedKeys, rowsData],
  );
  const handleBulkSelectionChange = useCallback(
    (id: string, nextSelected: boolean) => {
      const key = String(id ?? "").trim();
      if (!key) return;
      setBulkSelectedKeys((current) => {
        const next = new Set(current);
        if (nextSelected) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    },
    [],
  );

  const senderContactBadgeRow = useMemo(
    () => ({
      ...selectedDataRow,
      senderContactId:
        selectedDataRow?.senderContactId ??
        messageContent?.MESSAGE?.senderContactId,
      status:
        selectedDataRow?.status ?? messageContent?.MESSAGE?.status,
      type:
        selectedDataRow?.type ?? messageContent?.MESSAGE?.type,
    }),
    [
      messageContent?.MESSAGE?.senderContactId,
      messageContent?.MESSAGE?.status,
      messageContent?.MESSAGE?.type,
      selectedDataRow,
    ],
  );

  // Refs for useMessageOperations hook
  const filterParamRef = useRef({
    createdFrom: "",
    createdTo: "",
    sender: "",
    subject: "",
    newOnly: false,
    starred: false,
  });
  const offSetLimitRef = useRef<number>(0);
  const searchedTextRef = useRef<string>("");

  // Create refs object for useMessageOperations
  const refs = {
    filterParamRef,
    offSetLimitRef,
    searchedTextRef,
  };

  // Use the shared message operations hook
  const {
    getMessageListCount,
    loadMessages,
    getEmailContent,
    deleteMessage: deleteMessageOp,
    moveMessage: moveMessageOp,
    PAGE_SIZE,
  } = useMessageOperations({
    store,
    messageService,
    mainService,
    refs,
    treeHandle: selectedItem?.id, // Use treeHandle mode for MessageListComponent
  });

  let iconStyle = {
    paddingTop: "5px",
    paddingLeft: "5px",
    color: "#B0BEC5",
    WebkitTextStroke: ".5px",
  };

  // useEffect(() => {
  //   (async () => {
  //     setShowLoaderDetail(true);
  //     setSearchedValue('');
  //   })();
  // }, [selectedApp]);

  const handleNewMessageCreated = useCallback((event: AtollonEvent) => {
    // Reset all state
    setStopLoadData(false);
    setShowLoader(true);
    setShowLoaderDetail(true);
    setSelectedKeys(new Set());
    setSelectedDataRow(null);
    setBulkSelectedKeys(new Set());
    setMessageContent("");
    setNavigationIndex(0);
    setTotalMessageListRec(0);
    setRowsData([]);
    offSetLimitRef.current = 0;

    // Trigger reload by incrementing the trigger
    setReloadTrigger((prev) => prev + 1);
  }, []);

  // Effect to reload when trigger changes
  useEffect(() => {
    if (reloadTrigger > 0) {
      listMessageList.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTrigger]);

  useEffect(() => {
    listMessageList.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  useEffect(() => {
    subscribe(AtollonEvent.NEW_MESSAGE_CREATED, handleNewMessageCreated);
    return () => {
      unsubscribe(AtollonEvent.NEW_MESSAGE_CREATED, handleNewMessageCreated);
    };
  }, [handleNewMessageCreated]);

  // Wrapper for getEmailContent that also handles attachments for this component
  const getEmailContentAndAttachments = async (selectedRow: any) => {
    const messageContent = await getEmailContent(selectedRow);

    let emailAtts = messageContent.MESSAGE?.ATTACHMENT || [];
    if (!Array.isArray(emailAtts)) {
      emailAtts = [emailAtts];
    }

    const processedAttachments = emailAtts.map((file: any) => ({
      id: file.id,
      fileName: file.name,
      fileSize: file.size,
    }));

    setEmailAttachments(processedAttachments);
    return messageContent;
  };

  let listMessageList = useAsyncList<Character>({
    async load({ cursor, filterText }) {
      if (selectedApp === "") {
        setShowLoader(false);
        setShowLoaderDetail(false);
        return { items: rowsData, cursor: undefined };
      }
      if (cursor && Number(cursor) * PAGE_SIZE >= totalMessageListRec) {
        setShowLoader(false);
        setShowLoaderDetail(false);
        return { items: rowsData, cursor: undefined };
      }

      try {
        let totalRecords = await getMessageListCount(filterText || "");
        totalRecords = totalRecords ? Number(totalRecords) : 0;
        setTotalMessageListRec(totalRecords);

        let currentPage = cursor ? parseInt(cursor) : 0;
        let offSet = PAGE_SIZE * currentPage;
        offSetLimitRef.current = offSet;

        if (stopLoadData) {
          setShowLoader(false);
          setShowLoaderDetail(false);
          return { items: rowsData, cursor: undefined };
        }

        let listMessages = await loadMessages(filterText || "");
        setRowsData((prev: any) =>
          currentPage === 0 ? listMessages : [...prev, ...listMessages],
        );

        if (currentPage === 0 && listMessages.length > 0) {
          setSelectedKeys(new Set([listMessages[0].id]));
          setSelectedDataRow(listMessages[0]);
          let messageContent = await getEmailContentAndAttachments(
            listMessages[0],
          );
          setMessageContent(messageContent);
        }

        return {
          items: listMessages,
          cursor: String(offSet >= totalRecords ? undefined : currentPage + 1),
        };
      } catch (error) {
        console.error("Error loading messages:", error);
        return { items: [], cursor: undefined };
      } finally {
        setShowLoader(false);
        setShowLoaderDetail(false);
      }
    },
  });

  const resetMessageListState = (filterText: string) => {
    setStopLoadData(false);
    setShowLoader(true);
    setShowLoaderDetail(true);
    setSelectedKeys(new Set());
    setSelectedDataRow(null);
    setMessageContent("");
    setNavigationIndex(0);
    setTotalMessageListRec(0);
    setRowsData([]);
    offSetLimitRef.current = 0;
    listMessageList.setFilterText(filterText);
  };

  const searchMessageList = useCallback(async () => {
    filterParamRef.current = {
      createdFrom: "",
      createdTo: "",
      sender: "",
      subject: "",
      newOnly: false,
      starred: false,
    };
    resetMessageListState(searchedTextRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadMessageList = useCallback(() => {
    filterParamRef.current = {
      createdFrom: "",
      createdTo: "",
      sender: "",
      subject: "",
      newOnly: false,
      starred: false,
    };
    resetMessageListState("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterMessageList = useCallback(() => {
    resetMessageListState("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleListRowClick = async (rowId: any): Promise<void> => {
    const currentKey = rowId?.currentKey;
    if (!currentKey || selectedKeys?.has(currentKey)) return;

    setShowLoaderDetail(true);
    setSelectedKeys(rowId);

    const recIndex = rowsData.findIndex((item: any) => item.id === currentKey);
    setNavigationIndex(recIndex);

    const selectedRow = rowsData.find((item: any) => item.id === currentKey);
    if (!selectedRow) {
      setSelectedDataRow(null);
      setSelectedKeys(new Set());
      setShowLoaderDetail(false);
      return;
    }

    setSelectedDataRow(selectedRow);

    const messageContent = await getEmailContentAndAttachments(selectedRow);
    setMessageContent(messageContent);
    setShowLoaderDetail(false);
  };

  const handleListRowLargeClick = async (rowId: any): Promise<void> => {
    const currentKey = rowId?.currentKey || selectedDataRow?.id;
    if (!currentKey) return;

    setShowLoaderDetail(true);
    setSelectedKeys(rowId ?? selectedKeys);

    const recIndex = rowsData.findIndex((item: any) => item.id === currentKey);
    setNavigationIndex(recIndex);

    const selectedRow = rowsData.find((item: any) => item.id === currentKey);
    if (!selectedRow) {
      setSelectedDataRow(null);
      setSelectedKeys(new Set());
      setShowLoaderDetail(false);
      return;
    }

    setSelectedDataRow(selectedRow);

    const messageContent = await getEmailContentAndAttachments(selectedRow);
    setMessageContent(messageContent);
    setShowLoaderDetail(false);
    // Keep the list visible while 2+ rows are ticked so the user can keep
    // bulk-selecting without the detail pane flashing in on every highlight.
    if (bulkSelectedKeys.size < 2) {
      setShowDetail(true);
    }
  };

  const handleReplyClick = async (reply_type: string) => {
    const targetMessageId = getDiscussionTargetMessageId(
      selectedDataRow,
      messageContent?.MESSAGE,
    );
    const targetMessageContent =
      targetMessageId && targetMessageId !== String(selectedDataRow?.id || "")
        ? await getEmailContent({
            ...selectedDataRow,
            id: targetMessageId,
            seen: "1",
          })
        : messageContent;
    const detailMessage = targetMessageContent?.MESSAGE || {};
    const structuredRecipient =
      selectedDataRow?.RECIPIENT &&
      typeof selectedDataRow.RECIPIENT === "object"
        ? selectedDataRow.RECIPIENT
        : detailMessage.RECIPIENT &&
            typeof detailMessage.RECIPIENT === "object"
          ? detailMessage.RECIPIENT
          : selectedDataRow?.RECIPIENT || detailMessage.RECIPIENT;

    const messageForReply = {
      ...detailMessage,
      ...selectedDataRow,
      RECIPIENT: structuredRecipient,
      mailFrom: selectedDataRow?.mailFrom || detailMessage.mailFrom,
      sender: selectedDataRow?.sender || detailMessage.sender,
      senderContactId:
        selectedDataRow?.senderContactId || detailMessage.senderContactId,
      subject:
        selectedDataRow?.subject ||
        detailMessage.subject ||
        detailMessage.SUBJECT?.string,
      id: targetMessageId || selectedDataRow?.id,
      MESSAGE: targetMessageContent?.MESSAGE,
    };

    invokeReplyMessage(
      selectedItem?.id,
      reply_type,
      messageForReply,
      targetMessageContent?.MESSAGE?.ATTACHMENT
        ? Array.isArray(targetMessageContent.MESSAGE.ATTACHMENT)
          ? targetMessageContent.MESSAGE.ATTACHMENT
          : [targetMessageContent.MESSAGE.ATTACHMENT]
        : emailAttachments,
    );
  };

  const handleForwardClick = useCallback(async () => {
    const targetMessageId = getDiscussionTargetMessageId(
      selectedDataRow,
      messageContent?.MESSAGE,
    );

    const targetMessageContent =
      targetMessageId && targetMessageId !== String(selectedDataRow?.id || "")
        ? await getEmailContent({
            ...selectedDataRow,
            id: targetMessageId,
            seen: "1",
          })
        : messageContent;
    const detailMessage = targetMessageContent?.MESSAGE || {};
    const messageForForward = {
      ...detailMessage,
      ...selectedDataRow,
      id: targetMessageId || selectedDataRow?.id,
      MESSAGE: targetMessageContent?.MESSAGE,
    };

    invokeReplyMessage(
      selectedItem?.id,
      "FORWARD",
      messageForForward,
      targetMessageContent?.MESSAGE?.ATTACHMENT
        ? Array.isArray(targetMessageContent.MESSAGE.ATTACHMENT)
          ? targetMessageContent.MESSAGE.ATTACHMENT
          : [targetMessageContent.MESSAGE.ATTACHMENT]
        : emailAttachments,
    );
  }, [getEmailContent, messageContent, selectedDataRow, selectedItem?.id]);

  const handleCommentClick = useCallback(async () => {
    if (!selectedDataRow?.id) {
      ToastQueue.negative(
        t("no_message_selected", {
          ns: "layout_components",
          defaultValue: "Please select a message first.",
        }),
        { timeout: 2500 },
      );
      return;
    }
    const targetMessageId = getDiscussionTargetMessageId(
      selectedDataRow,
      messageContent?.MESSAGE,
    );
    const targetMessageContent =
      targetMessageId && targetMessageId !== String(selectedDataRow?.id || "")
        ? await getEmailContent({
            ...selectedDataRow,
            id: targetMessageId,
            seen: "1",
          })
        : messageContent;
    const detailMessage = targetMessageContent?.MESSAGE || {};
    const preservedFollowReference =
      selectedDataRow?.followReference ?? detailMessage?.followReference;
    const messageForComment = {
      // Start from list row for UI metadata, then overlay detail so context ids are not lost
      // (link rows can carry empty/0 context fields).
      ...selectedDataRow,
      ...detailMessage,
      id: targetMessageId || selectedDataRow?.id,
      followReference: preservedFollowReference,
      threadId:
        detailMessage?.threadId ??
        selectedDataRow?.threadId ??
        messageContent?.MESSAGE?.threadId,
      MESSAGE: targetMessageContent?.MESSAGE,
    };

    setCommentChatTarget(messageForComment);
    setIsCommentChatOpen(true);
  }, [getEmailContent, messageContent, selectedDataRow, t]);

  useEffect(() => {
    if (!isCommentChatOpen || !selectedDataRow?.id) {
      return;
    }

    let cancelled = false;

    (async () => {
      const targetMessageId = getDiscussionTargetMessageId(
        selectedDataRow,
        messageContent?.MESSAGE,
      );
      const targetMessageContent =
        targetMessageId && targetMessageId !== String(selectedDataRow?.id || "")
          ? await getEmailContent({
              ...selectedDataRow,
              id: targetMessageId,
              seen: "1",
            })
          : messageContent;
      const detailMessage = targetMessageContent?.MESSAGE || {};
      const preservedFollowReference =
        selectedDataRow?.followReference ?? detailMessage?.followReference;
      const nextCommentTarget = {
        ...selectedDataRow,
        ...detailMessage,
        id: targetMessageId || selectedDataRow?.id,
        followReference: preservedFollowReference,
        threadId:
          detailMessage?.threadId ??
          selectedDataRow?.threadId ??
          messageContent?.MESSAGE?.threadId,
        MESSAGE: targetMessageContent?.MESSAGE,
      };

      if (!cancelled) {
        setCommentChatTarget(nextCommentTarget);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    getEmailContent,
    isCommentChatOpen,
    messageContent,
    selectedDataRow,
  ]);

  const openResolveOptionsScreen = useCallback(() => {
    setIsResolveOptionsOpen(true);
  }, []);

  const openMoveDialog = useCallback(() => {
    setIsMoveDialogOpen(true);
  }, []);

  const handleMoveSelectedMessage = useCallback(
    async (targetContextId: string): Promise<boolean> => {
      const selectedRowsForMove =
        bulkSelectedRows.length > 0 ? bulkSelectedRows : [selectedDataRow];
      const moveTargets = selectedRowsForMove
        .map((row: any) => ({
          id: String(row?.id ?? "").trim(),
          doNotMoveThread: String(row?.threadId ?? "").trim() ? "0" : "1",
        }))
        .filter((row:any) => row.id);

      if (moveTargets.length === 0 || !targetContextId) {
        ToastQueue.negative(
          t("select_context_first", {
            ns: "layout_components",
            defaultValue: "Please select a context first",
          }),
          { timeout: 50 },
        );
        return false;
      }

      const results = await Promise.all(
        moveTargets.map((target:any) =>
          moveMessageOp(target.id, targetContextId, target.doNotMoveThread),
        ),
      );
      const movedCount = results.filter(Boolean).length;
      const success = movedCount === moveTargets.length;

      if (success) {
        setIsMoveDialogOpen(false);
        setBulkSelectedKeys(new Set());
        reloadMessageList();
        ToastQueue.info(
          t("message_moved_successfully", {
            ns: "layout_components",
            defaultValue:
              movedCount > 1
                ? "Messages moved successfully"
                : "Message moved successfully",
          }),
          { timeout: 50 },
        );
      } else {
        ToastQueue.negative(
          t("error_moving_message", {
            ns: "layout_components",
            defaultValue: "Error moving message",
          }),
          { timeout: 50 },
        );
      }

      return success;
    },
    [
      bulkSelectedRows,
      moveMessageOp,
      reloadMessageList,
      selectedDataRow,
      t,
    ],
  );

  const Loader = () => (
    <Flex width="100%" justifyContent="center" marginTop={10}>
      <ProgressCircle aria-label="Loading…" isIndeterminate />
    </Flex>
  );

  const NoResultsMessage = () => (
    <Flex
      direction="row"
      alignContent="center"
      justifyContent="center"
      marginTop={"30px"}
    >
      <IllustratedMessage>
        <NotFound />
        <Heading>{t("no_results", { ns: "layout_components" })}</Heading>
      </IllustratedMessage>
    </Flex>
  );

  const nextNavigation = async () => {
    setShowLoaderDetail(true);
    setSelectedKeys(new Set([rowsData[navigationIndex + 1].id]));
    let selectedRow = rowsData.find(
      (item: any) => item.id === rowsData[navigationIndex + 1].id,
    );
    if (!selectedRow) {
      return;
    }
    setSelectedDataRow(selectedRow);
    let messageContent = await getEmailContentAndAttachments(selectedRow);
    setMessageContent(messageContent);
    setShowLoaderDetail(false);
    setNavigationIndex((prev) => prev + 1);
  };

  const previousNavigation = async () => {
    setShowLoaderDetail(true);
    setSelectedKeys(new Set([rowsData[navigationIndex - 1].id]));

    let selectedRow = rowsData.find(
      (item: any) => item.id === rowsData[navigationIndex - 1].id,
    );
    if (!selectedRow) {
      return;
    }
    setSelectedDataRow(selectedRow);
    let messageContent = await getEmailContentAndAttachments(selectedRow);
    setMessageContent(messageContent);
    setShowLoaderDetail(false);
    setNavigationIndex((prev) => prev - 1);
  };

  const handleDeleteMessage = async (messageId: string) => {
    const success = await deleteMessageOp(messageId);

    if (success) {
      listMessageList.remove(messageId);
      setBulkSelectedKeys((current) => {
        if (!current.has(String(messageId))) return current;
        const next = new Set(current);
        next.delete(String(messageId));
        return next;
      });

      let nextIndex = rowsData.findIndex((item: any) => item.id === messageId);
      let nextMessageObj =
        rowsData.length >= nextIndex + 1 ? rowsData[nextIndex + 1] : null;

      setRowsData((prev: any) =>
        prev.filter((item: any) => item.id !== messageId),
      );

      if (nextMessageObj) {
        setSelectedKeys(new Set([nextMessageObj.id]));
        setSelectedDataRow(nextMessageObj);
        let messageContent =
          await getEmailContentAndAttachments(nextMessageObj);
        setMessageContent(messageContent);
      }

      ToastQueue.info(
        t("message_deleted_successfully", { ns: "layout_components" }),
        { timeout: 50 },
      );
    } else {
      ToastQueue.negative(
        t("error_deleting_message", { ns: "layout_components" }),
        { timeout: 50 },
      );
    }
  };

  const handleBulkDeleteMessages = useCallback(async () => {
    const ids = Array.from(bulkSelectedKeys);
    if (ids.length === 0) return;

    const results = await Promise.all(
      ids.map(async (id) => ({
        id,
        success: await deleteMessageOp(id),
      })),
    );
    const deletedIds = results
      .filter((result) => result.success)
      .map((result) => result.id);
    const failedCount = results.length - deletedIds.length;

    if (deletedIds.length > 0) {
      const deletedSet = new Set(deletedIds);
      deletedIds.forEach((id) => listMessageList.remove(id));
      setRowsData((prev: any[]) =>
        prev.filter((item: any) => !deletedSet.has(String(item.id))),
      );
      setBulkSelectedKeys(new Set());

      if (deletedSet.has(String(selectedDataRow?.id ?? ""))) {
        setSelectedDataRow({});
        setSelectedKeys(new Set());
        setMessageContent("");
      }

      ToastQueue.info(
        t("messages_deleted_successfully", {
          ns: "layout_components",
          defaultValue: `${deletedIds.length} messages deleted successfully`,
          count: deletedIds.length,
        }),
        { timeout: 50 },
      );
    }

    if (failedCount > 0) {
      ToastQueue.negative(
        t("error_deleting_messages", {
          ns: "layout_components",
          defaultValue: `${failedCount} messages could not be deleted`,
          count: failedCount,
        }),
        { timeout: 50 },
      );
    }
  }, [
    bulkSelectedKeys,
    deleteMessageOp,
    listMessageList,
    selectedDataRow?.id,
    t,
  ]);

  const MessageDialog = ({ close }: { close: () => void }) => (
    <Dialog size="S">
      <Flex UNSAFE_className={styles.delete_pop_up_parent}>
        <Content UNSAFE_className={styles.delete_pop_up_header}>
          {t("remove_message", { ns: "layout_components" })}
        </Content>
        <View UNSAFE_className={styles.delete_pop_up_line}></View>
        <View UNSAFE_className={styles.delete_pop_up_warning_text}>
          {t("are_you_sure_you_want_to_remove_message", {
            ns: "layout_components",
          })}
        </View>
        <View UNSAFE_className={styles.buttons_parent}>
          <View UNSAFE_className={styles.button_cancel}>
            <Link
              UNSAFE_className={styles.delete_pop_up_cancel_text}
              isQuiet
              onPress={() => {
                close();
              }}
            >
              {t("no_keep_it", { ns: "layout_components" })}
            </Link>
          </View>
          <View UNSAFE_className={styles.button_confirm}>
            <div className={styles.trash_icon}>
              <TrashIcon size={32} />
            </div>
            <Link
              UNSAFE_className={styles.delete_pop_up_confirm_text}
              isQuiet
              onPress={() => {
                handleDeleteMessage(selectedDataRow.id);
                close();
              }}
            >
              {t("yes_remove", { ns: "layout_components" })}
            </Link>
          </View>
        </View>
        <View UNSAFE_className={styles.ellipse_div}>
          <View UNSAFE_className={styles.delete_alert_icon}>
            <AlertIconFill size={72} />
          </View>
        </View>
      </Flex>
    </Dialog>
  );

  const RemoveMessageButton = () => (
    shouldConfirmDelete ? (
      <DialogTrigger isDismissable type="modal">
        <ActionButton UNSAFE_className={styles.btn_delete}>
          <Trash className={styles.icon_trash} size={24} />
        </ActionButton>
        {(close) => (
          <MessageDialog
            close={() => {
              close();
            }}
          />
        )}
      </DialogTrigger>
    ) : (
      <ActionButton
        UNSAFE_className={styles.btn_delete}
        onPress={() => {
          if (hasBulkSelection) {
            handleBulkDeleteMessages();
            return;
          }

          if (showDeleteAction && selectedDataRow?.id) {
            handleDeleteMessage(selectedDataRow.id);
          }
        }}
      >
        <Trash className={styles.icon_trash} size={24} />
      </ActionButton>
    )
  );

  const SearchDialog = ({ close }: { close: () => void }) => (
    <Dialog size="S" UNSAFE_className={styles.dialog_parent}>
      <Heading>
        <Flex alignItems="center" gap="size-100">
          <Text>{t("search", { ns: "layout_components" })}</Text>
        </Flex>
      </Heading>
      <Divider />
      <Content>
        <Form validationBehavior="native">
          <TextField
            autoFocus
            isRequired
            label={t("search", { ns: "layout_components" })}
            maxWidth="100%"
            onChange={(e) => {
              searchedTextRef.current = e;
            }}
            validationState="invalid"
          />
        </Form>
      </Content>
      <Footer>
        <Flex
          direction={"row"}
          width={"100%"}
          alignContent={"center"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={"size-200"}
        >
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            flexShrink={0}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.search_button_cancel}
          >
            <Link
              UNSAFE_className={styles.search_pop_up_cancel_text}
              isQuiet
              onPress={() => {
                close();
              }}
            >
              {t("cancel", { ns: "layout_components" })}
            </Link>
          </Flex>
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.search_button_confirm}
          >
            <Link
              UNSAFE_className={styles.search_pop_up_confirm_text}
              isQuiet
              onPress={() => {
                if (searchedTextRef.current === "") {
                  return;
                }
                searchMessageList();
                searchedTextRef.current = "";
                close();
              }}
            >
              {t("search", { ns: "layout_components" })}
            </Link>
          </Flex>
        </Flex>
      </Footer>
    </Dialog>
  );

  const SearchButton = () => (
    <DialogTrigger>
      <ActionButton UNSAFE_className={styles.no_border}>
        <SearchIcon size={24} />
      </ActionButton>
      {(close) => (
        <SearchDialog
          close={() => {
            close();
          }}
        />
      )}
    </DialogTrigger>
  );

  const FilterDialog = ({ close }: { close: () => void }) => (
    <Dialog size="M" UNSAFE_className={styles.dialog_parent}>
      <Heading>
        <Flex alignItems="center" gap="size-100">
          <Text>{t("Filter", { ns: "layout_components" })}</Text>
        </Flex>
      </Heading>
      <Divider />
      <Content>
        <Form>
          <Provider locale="en-US" UNSAFE_style={{ width: "100%" }}>
            <DatePicker
              width={"100%"}
              autoFocus
              id="dtCreatedFrom"
              label="Created from"
              onChange={(e) =>
                (filterParamRef.current.createdFrom = e?.toString() ?? "")
              }
            />
          </Provider>
          <Provider locale="en-US">
            <DatePicker
              width={"100%"}
              label="Created to"
              onChange={(e) =>
                (filterParamRef.current.createdTo = e?.toString() ?? "")
              }
            />
          </Provider>
          <TextField
            label="Sender"
            onChange={(e) => (filterParamRef.current.sender = e)}
          />
          <TextField
            label="Subject"
            onChange={(e) => (filterParamRef.current.subject = e)}
          />
          <Checkbox onChange={(e) => (filterParamRef.current.newOnly = e)}>
            Only new
          </Checkbox>
          <Checkbox onChange={(e) => (filterParamRef.current.starred = e)}>
            Only starred
          </Checkbox>
        </Form>
      </Content>
      <Footer>
        <Flex
          direction={"row"}
          width={"100%"}
          alignContent={"center"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={"size-200"}
        >
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            flexShrink={0}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.filter_button_cancel}
          >
            <Link
              UNSAFE_className={styles.filter_pop_up_cancel_text}
              isQuiet
              onPress={() => {
                close();
              }}
            >
              {t("cancel", { ns: "layout_components" })}
            </Link>
          </Flex>
          <Flex
            direction={"row"}
            width={"120px"}
            height={"40px"}
            alignItems={"center"}
            justifyContent={"center"}
            UNSAFE_className={styles.filter_button_confirm}
          >
            <Link
              UNSAFE_className={styles.filter_pop_up_confirm_text}
              isQuiet
              onPress={() => {
                filterMessageList();
                close();
              }}
            >
              {t("search", { ns: "layout_components" })}
            </Link>
          </Flex>
        </Flex>
      </Footer>
    </Dialog>
  );

  const FilterButton = () => (
    <DialogTrigger type="modal">
      <ActionButton UNSAFE_className={styles.no_border}>
        <FilterIcon UNSAFE_style={{ fill: "#b0bec5" }} />
      </ActionButton>
      {(close) => (
        <FilterDialog
          close={() => {
            close();
          }}
        />
      )}
    </DialogTrigger>
  );

  // Context message list intentionally does not support \"Move to context\".

  const handleMarkDone = async () => {
    if (!selectedDataRow?.id) {
      return;
    }

    await handleDeleteMessage(selectedDataRow.id);
  };

  const menuAction = (key: string) => {
    if (key === "move" && (hasBulkSelection || showMoveAction)) {
      openMoveDialog();
      return;
    }

    if (key === "markDone" && showMarkDoneAction) {
      handleMarkDone();
      return;
    }

    if (key === "delete" && hasBulkSelection) {
      handleBulkDeleteMessages();
      return;
    }

    if (key === "delete" && showDeleteAction && !shouldConfirmDelete) {
      handleDeleteMessage(selectedDataRow.id);
    }
  };

  const renderMessageList = () => {
    return showLoaderDetail ? (
      <View
        position={"relative"}
        width={"100%"}
        height={"400px"}
        overflow={"hidden"}
        UNSAFE_className={styles.menu_messages_large_parent}
      >
        <Loader />
      </View>
    ) : (
      <View
        position={"relative"}
        width={"100%"}
        height={"100vh"}
        overflow={"hidden"}
        UNSAFE_className={styles.menu_messages_large_parent}
      >
        <View
          position={"absolute"}
          width={"100%"}
          height={"64px"}
          overflow={"hidden"}
        >
          <Flex
            direction={"row"}
            position={"absolute"}
            top={24}
            height={"32px"}
            alignItems={"center"}
            justifyContent={"center"}
            marginStart={12}
            UNSAFE_className={styles.btn_new_email}
          >
            {/* {showNewMessageDialog && <NewMessage styles={styles} store={store} messageService={messageService} dialogVisibility={showNewMessageDialog} />} */}
            <Button
              variant="primary"
              UNSAFE_style={{
                background: "transparent",
                border: "none",
                width: "100%",
                fontSize: "14px",
              }}
              onPress={() => {
                //setShowNewMessageDialog(true);
                invokeNewMessageCreating(
                  selectedItem?.id,
                  "message",
                  undefined,
                  undefined,
                );
              }}
            >
              <>
                <Plus size={18} className={styles.btn_new_email_text} />
                <span className={styles.btn_new_email_text}>
                  {t("new_email", { ns: "layout_components" })}
                </span>
              </>
            </Button>
          </Flex>

          <Flex
            position={"absolute"}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"end"}
            gap={18}
            right={10}
            top={25}
          >
            <ArrowClockwise
              className={styles.message_list_header_icon}
              onClick={reloadMessageList}
            />
            {/* <SearchIcon size={24} /> */}
            <SearchButton />
            {/* <FilterIcon UNSAFE_className={styles.message_list_header_icon} /> */}
            <FilterButton />
            {bulkSelectedCount > 0 && (
              <span
                className={styles.bulk_count_chip}
                aria-live="polite"
                aria-label={t("bulk_selected_count", {
                  ns: "layout_components",
                  count: bulkSelectedCount,
                  defaultValue: "{{count}} selected",
                })}
              >
                {t("bulk_selected_count", {
                  ns: "layout_components",
                  count: bulkSelectedCount,
                  defaultValue: "{{count}} selected",
                })}
              </span>
            )}
            {showMoveAction && (
              <ActionButton
                UNSAFE_className={styles.no_border}
                onPress={openMoveDialog}
                aria-label={t("move", {
                  ns: "layout_components",
                  defaultValue: "Move",
                })}
              >
                <i className="bi bi-arrow-right-circle" style={{ fontSize: "1.6em", color: 'var(--neutral-50)' }} />
              </ActionButton>
            )}
            {hasBulkSelection && (
              <ActionButton
                UNSAFE_className={styles.no_border}
                onPress={() => handleBulkDeleteMessages()}
                aria-label={t("delete", {
                  ns: "layout_components",
                  defaultValue: "Delete",
                })}
              >
                <i className="bi bi-trash" style={{ fontSize: "1.3em", color: 'var(--neutral-50)' }} />
              </ActionButton>
            )}
          </Flex>
        </View>
        <View
          position={"absolute"}
          top={"65px"}
          left={"0px"}
          width={"100%"}
          height={"100vh"}
          overflow={"hidden"}
          UNSAFE_style={{
            fontSize: "12px",
            color: "#36474f",
            borderRadius: "16px",
            backgroundColor: "#fff",
          }}
        >
          <Flex
            direction={"column"}
            position={"absolute"}
            top={"0px"}
            left={"0px"}
            width={"100%"}
            alignItems={"start"}
            justifyContent={"start"}
            maxHeight={"100vh"}
          >
            <BulkSelectionProvider
              selectedKeys={bulkSelectedKeys}
              toggle={handleBulkSelectionChange}
            >
              <ListBox
                autoFocus
                id="listMessageCardsMobile"
                selectionMode="single"
                onSelectionChange={handleListRowLargeClick}
                defaultSelectedKeys={selectedKeys}
                selectedKeys={selectedKeys}
                aria-label="MessageListComponentMobile"
                items={listMessageList.items}
                isLoading={listMessageList.isLoading}
                onLoadMore={
                  totalMessageListRec > rowsData.length
                    ? listMessageList.loadMore
                    : undefined
                }
                width={"100%"}
                UNSAFE_style={{ padding: "0 0 0 5px" }}
              >
                {(item) => (
                  <Item key={item.id}>
                    {isMobile ? (
                      <CardComponent item={item} />
                    ) : (
                      <CardComponentLarge item={item} />
                    )}
                  </Item>
                )}
              </ListBox>
            </BulkSelectionProvider>
          </Flex>
        </View>
      </View>
    );
  };

  const renderMessageListDetailLarge = () => {
    if (isBulkDetailSuppressed) {
      return (
        <View
          width={"100%"}
          position={"relative"}
          height={"100vh"}
          overflow={"hidden"}
          UNSAFE_className={styles.message_list_detail_wrapper_large}
        >
          <BulkSelectionPlaceholder
            count={bulkSelectedCount}
            stylesMap={styles}
            label={t("messages_selected", {
              ns: "layout_components",
              count: bulkSelectedCount,
              defaultValue: "messages selected",
            })}
          />
        </View>
      );
    }
    return (
      <View
        width={"100%"}
        position={"relative"}
        height={"100vh"}
        overflow={"hidden"}
        UNSAFE_className={styles.message_list_detail_wrapper_large}
      >
        <Flex
          direction={"column"}
          position={"absolute"}
          top={0}
          left={0}
          width={"100%"}
          height={"98%"}
          alignItems={"start"}
          justifyContent={"start"}
          UNSAFE_className={styles.message_list_detail_parent}
        >
          <Flex
            direction={"column"}
            height={"98%"}
            alignSelf={"stretch"}
            alignItems={"center"}
            justifyContent={"start"}
          >
            <View
              position={"relative"}
              top={0}
              right={0}
              width={"100%"}
              height={"64px"}
              overflow={"hidden"}
              UNSAFE_className={styles.email_content_header_parent_large}
            >
              <Flex
                direction={"row"}
                position={"absolute"}
                top={"24px"}
                left={"10px"}
                justifyContent={"start"}
                gap={"18px"}
                alignItems={"center"}
              >
                <Flex
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"12px"}
                >
                  <View marginStart={"5px"} marginEnd={"3px"}>
                    <ArrowLeft
                      size={24}
                      className={styles.message_list_header_icon}
                      onClick={() => {
                        setShowDetail(false);
                      }}
                    />
                  </View>
                  {showReplyAction && (
                    <Flex
                      direction={"row"}
                      width={"34px"}
                      height={"34px"}
                      flexShrink={0}
                      justifyContent={"center"}
                      UNSAFE_className={styles.action_button}
                    >
                      <ActionButton
                        onPress={() => {
                          handleReplyClick("REPLY");
                        }}
                        UNSAFE_className={styles.action_button}
                        aria-label="Reply"
                      >
                        <ReplyIcon size={24} />
                      </ActionButton>
                    </Flex>
                  )}
                  {showReplyAction && (
                    <Flex
                      direction={"row"}
                      width={"34px"}
                      height={"34px"}
                      flexShrink={0}
                      justifyContent={"center"}
                      UNSAFE_className={styles.action_button}
                    >
                      <ActionButton
                        onPress={() => {
                          handleReplyClick("REPLYALL");
                        }}
                        UNSAFE_className={styles.action_button}
                        aria-label="Reply all"
                      >
                        <ReplyAllIcon size={24} />
                      </ActionButton>
                    </Flex>
                  )}
                  {showForwardAction && (
                    <Flex
                      direction={"row"}
                      width={"34px"}
                      height={"34px"}
                      flexShrink={0}
                      justifyContent={"center"}
                      UNSAFE_className={styles.action_button}
                    >
                      <ActionButton
                        onPress={() => {
                          handleForwardClick();
                        }}
                        UNSAFE_className={styles.action_button}
                        aria-label="Forward"
                      >
                        <ArrowForwardIcon size={24} />
                      </ActionButton>
                    </Flex>
                  )}
                </Flex>
                {showCommentAction && (
                  <>
                    {(showReplyAction || showForwardAction) && (
                      <View
                        position={"relative"}
                        width={"1px"}
                        height={"24px"}
                        UNSAFE_className={styles.message_list_detail_header_divider}
                      ></View>
                    )}
                    <Flex
                      direction={"row"}
                      height={"32px"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      width={"100px"}
                      UNSAFE_className={styles.btn_comment}
                    >
                      <button
                        className={styles.btn_comment_text}
                        style={{
                          background: "transparent",
                          border: "none",
                          width: "100%",
                          fontSize: "14px",
                        }}
                        onClick={() => handleCommentClick()}
                      >
                        {t("comment", { ns: "layout_components" })}
                      </button>
                    </Flex>
                  </>
                )}
              </Flex>
              <Flex
                direction={"row"}
                position={"absolute"}
                top={"27px"}
                right={"20px"}
                alignItems={"center"}
                justifyContent={"start"}
                gap={"10px"}
              >
                {showDeleteAction && <RemoveMessageButton />}
                {showMoveAction && (
                  <ActionButton
                    UNSAFE_className={styles.no_border}
                    onPress={openMoveDialog}
                    aria-label={t("move", {
                      ns: "layout_components",
                      defaultValue: "Move",
                    })}
                  >
                    <i className="bi bi-arrow-right-circle" />
                  </ActionButton>
                )}
              </Flex>
            </View>
            {(showResolveAction || showMarkDoneAction) && (
              <Flex
                direction={"row"}
                width={"100%"}
                alignItems={"center"}
                justifyContent={"start"}
                UNSAFE_style={{
                  padding: "8px 16px 0 16px",
                  flexShrink: 0,
                }}
              >
                <button
                  type="button"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    minHeight: "36px",
                    padding: "0 14px",
                    borderRadius: "999px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    border: showMarkDoneAction
                      ? "1px solid #1976d2"
                      : "1px solid #ff9800",
                    color: showMarkDoneAction ? "#1976d2" : "#ff9800",
                    background: showMarkDoneAction ? "#f5f9ff" : "#fff8ef",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => {
                    if (showMarkDoneAction) {
                      handleMarkDone();
                      return;
                    }

                    openResolveOptionsScreen();
                  }}
                >
                  {showMarkDoneAction ? (
                    <Check2Circle size={16} />
                  ) : (
                    <QuestionCircle size={16} />
                  )}
                  <span>
                    {showMarkDoneAction
                      ? t("mark_as_done", {
                          ns: "layout_components",
                          defaultValue: "Mark as done",
                        })
                      : t("Resolve message", {
                          ns: "layout_components",
                          defaultValue: "Resolve message",
                        })}
                  </span>
                </button>
              </Flex>
            )}
            <Flex
              direction={"column"}
              width={"100%"}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={styles.email_content_header_parent_large}
            >
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                alignItems={"start"}
                justifyContent={"start"}
                gap={"18px"}
                UNSAFE_style={{
                  borderBottom: "1px solid #eceff1",
                  paddingLeft: "13px",
                  paddingBottom: "5px",
                }}
              >
                <View UNSAFE_className={styles.user_img}>
                  <img
                    id="img_renderMessageListDetailLarge"
                    className={styles.avatar_desktop_light}
                    alt=""
                    src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                  />
                </View>
                <Flex
                  direction={"column"}
                  width={"100%"}
                  alignItems={"start"}
                  justifyContent={"start"}
                  gap={"5px"}
                >
                  <Flex
                    direction={"row"}
                    alignSelf={"stretch"}
                    alignItems={"end"}
                    justifyContent={"space-between"}
                  >
                    <Flex
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"start"}
                      gap={"8px"}
                    >
                      <View UNSAFE_className={styles.user_full_name}>
                        {getSenderName(selectedDataRow.sender)}
                      </View>
                      <View
                        width={"1px"}
                        position={"relative"}
                        height={"12px"}
                        UNSAFE_className={styles.user_detail_divider}
                      ></View>
                      <View UNSAFE_className={styles.user_email}>
                        {getSenderEmail(selectedDataRow.mailFrom)}
                      </View>
                    </Flex>
                    <View
                      position={"absolute"}
                      right={"25px"}
                      UNSAFE_className={styles.div_date}
                    >
                      {moment(selectedDataRow.date).format("DD.MM.YYYY HH:mm")}
                    </View>
                  </Flex>
                  <Flex
                    direction={"column"}
                    alignSelf={"stretch"}
                    alignItems={"start"}
                    justifyContent={"start"}
                    gap={"8px"}
                    UNSAFE_className={styles.subject_parent}
                  >
                    <View
                      position={"relative"}
                      alignSelf={"stretch"}
                      UNSAFE_className={styles.email_subject}
                    >
                      {selectedDataRow.subject}
                    </View>
                    <RecipientToField recipient={selectedDataRow?.RECIPIENT} />
                    <ContextPathLinks
                      message={messageContent.MESSAGE}
                      styles={styles}
                    />
                    <RecipientNotifyField
                      recipient={messageContent?.MESSAGE?.RECIPIENT}
                    />
                    {messageContent?.participantsName &&
                      messageContent.type === "12" && (
                        <Flex
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          gap={"6px"}
                        >
                          <View UNSAFE_className={styles.address}>
                            {t("participants", {
                              ns: "layout_components",
                              defaultValue: "Participants",
                            })}
                            : {messageContent.participantsName}
                          </View>
                        </Flex>
                      )}
                    <MessageAttachment
                      store={store}
                      styles={styles}
                      messageContent={messageContent}
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            <View
              width={"93%"}
              position={"relative"}
              UNSAFE_className={styles.email_content}
              height={"75%"}
            >
              {/* <div
                dangerouslySetInnerHTML={{
                  __html: oldEmailContentFix(
                    messageContent.MESSAGE?.PREVIEW?.content
                  ),
                }}
              /> */}
              <iframe
                srcDoc={oldEmailContentFix(
                  messageContent.MESSAGE?.PREVIEW?.content,
                )}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  backgroundColor: "#fff",
                }}
                title="Email Content"
                sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
              />
            </View>
          </Flex>
        </Flex>
        <Flex
          direction={"column"}
          position={"absolute"}
          bottom={"16px"}
          left={"calc(50% - 128.5px)"}
          alignItems={"center"}
          justifyContent={"end"}
        >
          <Flex
            direction={"row"}
            width={"258px"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={"32px"}
          >
            {navigationIndex > 0 && (
              <Flex
                direction={"row"}
                height={"32px"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"8px"}
                UNSAFE_className={styles.btn_previous}
              >
                <Button
                  variant="primary"
                  onPress={previousNavigation}
                  UNSAFE_style={{
                    background: "transparent",
                    border: "none",
                    width: "100%",
                  }}
                >
                  <>
                    <ArrowLeft className={styles.icon_navigate} />
                    &nbsp;&nbsp;&nbsp;
                    <View UNSAFE_className={styles.btn_text}>
                      {t("previous", { ns: "layout_components" })}
                    </View>
                  </>
                </Button>
              </Flex>
            )}
            {navigationIndex < rowsData.length - 1 && (
              <Flex
                direction={"row"}
                width={"113px"}
                height={"32px"}
                flexShrink={0}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"8px"}
                UNSAFE_className={styles.btn_next}
              >
                <Button
                  variant="primary"
                  onPress={nextNavigation}
                  UNSAFE_style={{
                    background: "transparent",
                    border: "none",
                    width: "100%",
                  }}
                >
                  <>
                    <View UNSAFE_className={styles.btn_text}>
                      {t("next", { ns: "layout_components" })}
                    </View>
                    &nbsp;&nbsp;&nbsp;
                    <ArrowRight className={styles.icon_navigate} />
                  </>
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>
      </View>
    );
  };

  const renderMessageListDetailMobile = () => {
    if (isBulkDetailSuppressed) {
      return (
        <View
          width={"100%"}
          position={"relative"}
          height={"100vh"}
          overflow={"hidden"}
          UNSAFE_className={styles.message_list_detail_wrapper_large}
        >
          <BulkSelectionPlaceholder
            count={bulkSelectedCount}
            stylesMap={styles}
            label={t("messages_selected", {
              ns: "layout_components",
              count: bulkSelectedCount,
              defaultValue: "messages selected",
            })}
          />
        </View>
      );
    }
    return (
      <View
        width={"100%"}
        position={"relative"}
        height={"100vh"}
        overflow={"hidden"}
        UNSAFE_className={styles.message_list_detail_wrapper_large}
      >
        <Flex
          direction={"column"}
          position={"absolute"}
          top={0}
          left={0}
          width={"100%"}
          height={"98%"}
          alignItems={"start"}
          justifyContent={"start"}
          UNSAFE_className={styles.message_list_detail_parent}
        >
          <Flex
            direction={"column"}
            height={"98%"}
            alignSelf={"stretch"}
            alignItems={"center"}
            justifyContent={"start"}
          >
            <View
              position={"relative"}
              top={0}
              right={0}
              width={"100%"}
              height={"64px"}
              overflow={"hidden"}
              UNSAFE_className={styles.email_content_header_parent_large}
            >
              <Flex
                direction={"row"}
                position={"absolute"}
                top={"25px"}
                left={"10px"}
                justifyContent={"start"}
                gap={"18px"}
                alignItems={"center"}
              >
                <Flex
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"12px"}
                >
                  <View marginStart={"5px"} marginEnd={"3px"}>
                    <ArrowLeft
                      size={24}
                      className={styles.message_list_header_icon}
                      onClick={() => {
                        setShowDetail(false);
                      }}
                    />
                  </View>
                  <Flex
                    direction={"row"}
                    width={"34px"}
                    height={"34px"}
                    flexShrink={0}
                    justifyContent={"center"}
                    UNSAFE_className={styles.action_button}
                  >
                    <ActionButton
                      onPress={() => {
                        handleReplyClick("REPLY");
                      }}
                      UNSAFE_className={styles.action_button}
                      aria-label="Reply"
                    >
                      <ReplyIcon size={24} />
                    </ActionButton>
                  </Flex>
                  <Flex
                    direction={"row"}
                    width={"34px"}
                    height={"34px"}
                    flexShrink={0}
                    justifyContent={"center"}
                    UNSAFE_className={styles.action_button}
                  >
                    <ActionButton
                      onPress={() => {
                        handleReplyClick("REPLYALL");
                      }}
                      UNSAFE_className={styles.action_button}
                      aria-label="Reply all"
                    >
                      <ReplyAllIcon size={24} />
                    </ActionButton>
                  </Flex>
                  {showForwardAction && (
                    <Flex
                      direction={"row"}
                      width={"34px"}
                      height={"34px"}
                      flexShrink={0}
                      justifyContent={"center"}
                      UNSAFE_className={styles.action_button}
                    >
                      <ActionButton
                        onPress={() => {
                          handleForwardClick();
                        }}
                        UNSAFE_className={styles.action_button}
                        aria-label="Forward"
                      >
                        <ArrowForwardIcon size={24} />
                      </ActionButton>
                    </Flex>
                  )}
                </Flex>
                <View
                  position={"relative"}
                  width={"1px"}
                  height={"24px"}
                  UNSAFE_className={styles.message_list_detail_header_divider}
                ></View>
                <Flex
                  direction={"row"}
                  height={"32px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  width={"100px"}
                  UNSAFE_className={styles.btn_comment}
                  UNSAFE_style={{ display: "none" }}
                >
                  <button
                    className={styles.btn_comment_text}
                    style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                      fontSize: "14px",
                    }}
                    onClick={() => handleCommentClick()}
                  >
                    {t("comment", { ns: "layout_components" })}
                  </button>
                </Flex>
              </Flex>
              <Flex
                direction={"row"}
                position={"absolute"}
                top={"32px"}
                right={"2px"}
                alignItems={"center"}
                justifyContent={"start"}
              >
                {/* <RemoveMessageButton /> */}
                <MenuTrigger>
                  <Link>
                    <span aria-label="Open context menu" role="button">
                      <i className="bi bi-three-dots fs-7"></i>
                    </span>
                  </Link>
                  <Menu width={"100%"} onAction={(key: any) => menuAction(key)}>
                    {showMoveAction ? (
                      <Item key="move" textValue="move">
                        <i className="bi bi-arrow-right-circle" style={iconStyle}></i>
                        &nbsp;&nbsp;
                        <span
                          style={{
                            color: "#36474F",
                            paddingLeft: "5px",
                            paddingTop: "4px",
                          }}
                        >
                          {t("move", {
                            ns: "layout_components",
                            defaultValue: "Move",
                          })}
                        </span>
                      </Item>
                    ) : null}
                    {showMarkDoneAction ? (
                      <Item key="markDone" textValue="mark as done">
                        <i
                          className="bi bi-check2-circle"
                          style={iconStyle}
                        ></i>
                        &nbsp;&nbsp;
                        <span
                          style={{
                            color: "#36474F",
                            paddingLeft: "5px",
                            paddingTop: "4px",
                          }}
                        >
                          {t("mark_as_done", {
                            ns: "layout_components",
                            defaultValue: "Mark as done",
                          })}
                        </span>
                      </Item>
                    ) : null}
                  </Menu>
                </MenuTrigger>
              </Flex>
            </View>
            <Flex
              direction={"column"}
              width={"100%"}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={styles.email_content_header_parent_large}
            >
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                alignItems={"start"}
                justifyContent={"start"}
                gap={"18px"}
                UNSAFE_style={{
                  borderBottom: "1px solid #eceff1",
                  paddingLeft: "13px",
                  paddingBottom: "15px",
                }}
              >
                <View UNSAFE_className={styles.user_img}>
                  <img
                    id="img_renderMessageListDetailMobile"
                    className={styles.avatar_desktop_light}
                    alt=""
                    src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                  />
                </View>
                <Flex
                  direction={"column"}
                  width={"100%"}
                  alignItems={"start"}
                  justifyContent={"start"}
                  gap={"5px"}
                >
                  <Flex
                    direction={"row"}
                    alignSelf={"stretch"}
                    alignItems={"end"}
                    justifyContent={"space-between"}
                  >
                    <Flex
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"start"}
                      gap={"8px"}
                    >
                      <View UNSAFE_className={styles.user_full_name}>
                        {getSenderName(selectedDataRow.sender)}
                      </View>
                      <View
                        width={"1px"}
                        position={"relative"}
                        height={"12px"}
                        UNSAFE_className={styles.user_detail_divider}
                      ></View>
                      <View UNSAFE_className={styles.user_email}>
                        {getSenderEmail(selectedDataRow.mailFrom)}
                      </View>
                    </Flex>
                  </Flex>
                  <Flex
                    direction={"column"}
                    alignSelf={"stretch"}
                    alignItems={"start"}
                    justifyContent={"start"}
                    gap={"8px"}
                    UNSAFE_className={styles.subject_parent}
                  >
                    <View
                      position={"relative"}
                      alignSelf={"stretch"}
                      UNSAFE_className={styles.email_subject}
                    >
                      {selectedDataRow.subject}
                    </View>
                    <RecipientToField recipient={selectedDataRow?.RECIPIENT} />
                    <ContextPathLinks
                      message={messageContent.MESSAGE}
                      styles={styles}
                    />
                    <RecipientNotifyField
                      recipient={messageContent?.MESSAGE?.RECIPIENT}
                    />
                    <Flex
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      gap={"6px"}
                    >
                      {messageContent?.participantsName && (
                        <View UNSAFE_className={styles.address}>
                          {t("participants", {
                            ns: "layout_components",
                            defaultValue: "Participants",
                          })}
                          : {messageContent.participantsName}
                        </View>
                      )}
                    </Flex>

                    <View
                      position={"relative"}
                      UNSAFE_className={styles.div_date}
                    >
                      {moment(selectedDataRow.date).format("DD.MM.YYYY HH:mm")}
                    </View>
                    <MessageAttachment
                      store={store}
                      styles={styles}
                      messageContent={messageContent}
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            <View
              width={"93%"}
              position={"relative"}
              UNSAFE_className={styles.email_content}
              height={"55%"}
            >
              {/* <div
                dangerouslySetInnerHTML={{
                  __html: oldEmailContentFix(
                    messageContent.MESSAGE?.PREVIEW?.content
                  ),
                }}
              /> */}
              <iframe
                srcDoc={oldEmailContentFix(
                  messageContent.MESSAGE?.PREVIEW?.content,
                )}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  backgroundColor: "#fff",
                }}
                title="Email Content"
                sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
              />
            </View>
          </Flex>
        </Flex>
        <Flex
          direction={"column"}
          position={"absolute"}
          bottom={"55px"}
          left={"calc(50% - 128.5px)"}
          alignItems={"center"}
          justifyContent={"end"}
        >
          <Flex
            direction={"row"}
            width={"258px"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={"32px"}
          >
            {navigationIndex > 0 && (
              <Flex
                direction={"row"}
                height={"32px"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"8px"}
                UNSAFE_className={styles.btn_previous}
              >
                <Button
                  variant="primary"
                  onPress={previousNavigation}
                  UNSAFE_style={{
                    background: "transparent",
                    border: "none",
                    width: "100%",
                  }}
                >
                  <>
                    <ArrowLeft className={styles.icon_navigate} />
                    &nbsp;&nbsp;&nbsp;
                    <View UNSAFE_className={styles.btn_text}>
                      {t("previous", { ns: "layout_components" })}
                    </View>
                  </>
                </Button>
              </Flex>
            )}
            {navigationIndex < rowsData.length - 1 && (
              <Flex
                direction={"row"}
                width={"113px"}
                height={"32px"}
                flexShrink={0}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"8px"}
                UNSAFE_className={styles.btn_next}
              >
                <Button
                  variant="primary"
                  onPress={nextNavigation}
                  UNSAFE_style={{
                    background: "transparent",
                    border: "none",
                    width: "100%",
                  }}
                >
                  <>
                    <View UNSAFE_className={styles.btn_text}>
                      {t("next", { ns: "layout_components" })}
                    </View>
                    &nbsp;&nbsp;&nbsp;
                    <ArrowRight className={styles.icon_navigate} />
                  </>
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>
      </View>
    );
  };

  const renderContent = () => {
    if (!showDetail) {
      return renderMessageList();
    } else if (isMobile) {
      return renderMessageListDetailMobile();
    } else {
      return renderMessageListDetailLarge();
    }
  };

  const oldEmailContentFix = (emailContent: any) => {
    //remove padding: 16px; from the emailContent string
    const regex = /padding:\s*16px;/g;
    const fixedContent = emailContent?.replace(regex, "");

    return fixedContent;
  };

  if (showLoader) {
    return <Loader />;
  } else {
    return (
      <>
        {rowsData?.length > 0 ? (
          <div ref={componentRef}>
            {isMobile ||
            (componentRef.current && componentRef.current.offsetWidth < 780) ? (
              renderContent()
            ) : (
              <View
                width={"100%"}
                position={"relative"}
                height={"889px"}
                UNSAFE_className={styles.message_list_wrapper}
              >
                <View
                  position={"relative"}
                  top={0}
                  left={0}
                  width={"35%"}
                  height={"100%"}
                  overflow={"hidden"}
                  UNSAFE_className={styles.message_list_parent}
                >
                  <Flex
                    position={"relative"}
                    top={0}
                    left={0}
                    direction={"column"}
                    alignItems={"start"}
                    justifyContent={"start"}
                  >
                    <View
                      position={"relative"}
                      top={0}
                      left={0}
                      width={"100%"}
                      height={"64px"}
                      overflow={"hidden"}
                      UNSAFE_className={styles.messages_list_header_parent}
                    >
                      <Flex
                        direction={"row"}
                        position={"absolute"}
                        top={24}
                        height={"32px"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        marginStart={12}
                        UNSAFE_className={styles.btn_new_email}
                      >
                        {/* {showNewMessageDialog && <NewMessage styles={styles} store={store} messageService={messageService} dialogVisibility={showNewMessageDialog} />} */}
                        <Button
                          variant="primary"
                          UNSAFE_style={{
                            background: "transparent",
                            border: "none",
                            width: "100%",
                            fontSize: "14px",
                          }}
                          onPress={() => {
                            //setShowNewMessageDialog(true);
                            invokeNewMessageCreating(
                              selectedItem?.id,
                              "message",
                              undefined,
                              undefined,
                            );
                          }}
                        >
                          <>
                            <Plus
                              size={18}
                              className={styles.btn_new_email_text}
                            />
                            <span className={styles.btn_new_email_text}>
                              {t("new_email", { ns: "layout_components" })}
                            </span>
                          </>
                        </Button>
                      </Flex>
                      <Flex
                        position={"relative"}
                        marginTop={"25px"}
                        UNSAFE_style={{ float: "right" }}
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"start"}
                        gap={15}
                      >
                        <ArrowClockwise
                          className={styles.message_list_header_icon}
                          onClick={reloadMessageList}
                        />
                        {/* <SearchIcon size={24} /> */}
                        <SearchButton />
                        {/* <FilterIcon UNSAFE_className={styles.message_list_header_icon} /> */}
                        <FilterButton />
                        {bulkSelectedCount > 0 && (
                          <span
                            className={styles.bulk_count_chip}
                            aria-live="polite"
                            aria-label={t("bulk_selected_count", {
                              ns: "layout_components",
                              count: bulkSelectedCount,
                              defaultValue: "{{count}} selected",
                            })}
                          >
                            {t("bulk_selected_count", {
                              ns: "layout_components",
                              count: bulkSelectedCount,
                              defaultValue: "{{count}} selected",
                            })}
                          </span>
                        )}
                        {showMoveAction && (
                          <ActionButton
                            UNSAFE_className={styles.no_border}
                            onPress={openMoveDialog}
                            aria-label={t("move", {
                              ns: "layout_components",
                              defaultValue: "Move",
                            })}
                          >
                            <i className="bi bi-arrow-right-circle" style={{ fontSize: "1.6em", color: 'var(--neutral-50)' }} />
                          </ActionButton>
                        )}
                        {hasBulkSelection && (
                          <ActionButton
                            UNSAFE_className={styles.no_border}
                            onPress={() => handleBulkDeleteMessages()}
                            aria-label={t("delete", {
                              ns: "layout_components",
                              defaultValue: "Delete",
                            })}
                          >
                            <i className="bi bi-trash" style={{ fontSize: "1.3em", color: 'var(--neutral-50)' }} />
                          </ActionButton>
                        )}
                      </Flex>
                    </View>
                    <Flex maxHeight="100vh" width={"100%"}>
                      <BulkSelectionProvider
                        selectedKeys={bulkSelectedKeys}
                        toggle={handleBulkSelectionChange}
                      >
                        <ListBox
                          autoFocus
                          id="listMessageCards"
                          selectionMode="single"
                          onSelectionChange={handleListRowClick}
                          defaultSelectedKeys={selectedKeys}
                          selectedKeys={selectedKeys}
                          aria-label="MessageListComponent"
                          items={listMessageList.items}
                          isLoading={listMessageList.isLoading}
                          onLoadMore={
                            totalMessageListRec > rowsData.length
                              ? listMessageList.loadMore
                              : undefined
                          }
                          width={"100%"}
                          UNSAFE_style={{ padding: "0 0 0 5px" }}
                        >
                          {(item) => (
                            <Item key={item.id}>
                              <CardComponent item={item} />
                            </Item>
                          )}
                        </ListBox>
                      </BulkSelectionProvider>
                    </Flex>
                  </Flex>
                </View>
                <View
                  position={"absolute"}
                  top={0}
                  left={"35%"}
                  width={"1px"}
                  height={"100%"}
                  UNSAFE_className={styles.message_list_divider}
                ></View>
                {showLoaderDetail || listMessageList.isLoading ? (
                  <View
                    position={"absolute"}
                    top={15}
                    left={"36%"}
                    width={"65%"}
                    height={"98%"}
                    overflow={"hidden"}
                    UNSAFE_className={styles.message_list_detail_wrapper}
                  >
                    <Loader />
                  </View>
                ) : isBulkDetailSuppressed ? (
                  <View
                    position={"absolute"}
                    top={15}
                    left={"36%"}
                    width={"64%"}
                    height={"98%"}
                    overflow={"hidden"}
                    UNSAFE_className={styles.message_list_detail_wrapper}
                  >
                    <BulkSelectionPlaceholder
                      count={bulkSelectedCount}
                      stylesMap={styles}
                      label={t("messages_selected", {
                        ns: "layout_components",
                        count: bulkSelectedCount,
                        defaultValue: "messages selected",
                      })}
                    />
                  </View>
                ) : (
                  <div
                    role="main"
                    aria-label="Message detail"
                    className={styles.message_list_detail_wrapper}
                    style={{
                      position: "absolute",
                      top: 15,
                      left: "36%",
                      width: "64%",
                      height: "98%",
                      overflow: "hidden",
                    }}
                  >
                    {/* Structured, accessible message detail layout */}
                    <Flex
                      direction="column"
                      height="100%"
                      UNSAFE_className={styles.message_list_detail_parent}
                    >
                      {(showResolveAction || showMarkDoneAction) && (
                        <Flex
                          direction="row"
                          width="100%"
                          alignItems="center"
                          justifyContent="start"
                          UNSAFE_style={{
                            padding: "12px 16px 0 16px",
                            flexShrink: 0,
                            backgroundColor: "#fff",
                          }}
                        >
                          <button
                            type="button"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "12px",
                              width: "100%",
                              minHeight: "46px",
                              padding: "0 16px",
                              borderRadius: "12px",
                              fontSize: "14px",
                              fontWeight: 600,
                              cursor: "pointer",
                              border: showMarkDoneAction
                                ? "1px solid #1976d2"
                                : "1px solid #ff9800",
                              color: showMarkDoneAction
                                ? "#1976d2"
                                : "#ff9800",
                              background: showMarkDoneAction
                                ? "#f5f9ff"
                                : "#fff8ef",
                            }}
                            onClick={() => {
                              if (showMarkDoneAction) {
                                handleMarkDone();
                                return;
                              }

                              openResolveOptionsScreen();
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              {showMarkDoneAction ? (
                                <Check2Circle size={18} />
                              ) : (
                                <QuestionCircle size={18} />
                              )}
                              <span>
                                {showMarkDoneAction
                                  ? t("mark_as_done", {
                                      ns: "layout_components",
                                      defaultValue: "Mark as done",
                                    })
                                  : t("Resolve message", {
                                      ns: "layout_components",
                                      defaultValue: "Resolve message",
                                    })}
                              </span>
                            </span>
                            <ArrowRight size={18} />
                          </button>
                        </Flex>
                      )}
                      {/* Header / Actions */}
                      <div
                        className={styles.message_list_detail_header_parent}
                        role="toolbar"
                        aria-label="Message actions"
                      >
                        <Flex
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          UNSAFE_style={{ padding: "10px 16px" }}
                        >
                          <Flex direction="row" alignItems="center" gap="12px">
                            {isMobile && (
                              <ActionButton
                                UNSAFE_className={styles.icon_back}
                                onPress={() => setShowDetail(false)}
                                aria-label="Back"
                              >
                                <ArrowLeft size={20} />
                              </ActionButton>
                            )}
                            {showReplyAction && (
                              <ActionButton
                                onPress={() => {
                                  handleReplyClick("REPLY");
                                }}
                                UNSAFE_className={styles.action_button}
                                aria-label="Reply"
                              >
                                <ReplyIcon size={18} />
                              </ActionButton>
                            )}

                            {showReplyAction && (
                              <ActionButton
                                onPress={() => {
                                  handleReplyClick("REPLYALL");
                                }}
                                UNSAFE_className={styles.action_button}
                                aria-label="Reply all"
                              >
                                <ReplyAllIcon size={18} />
                              </ActionButton>
                            )}
                            {showForwardAction && (
                              <>
                              <ActionButton
                                UNSAFE_className={styles.action_button}
                                aria-label="Forward"
                                onPress={() => {
                                  handleForwardClick();
                                }}
                              >
                                <ArrowForwardIcon size={18} />
                              </ActionButton>
                              <View
                                  UNSAFE_style={{
                                    width: "1px",
                                    height: "24px",
                                    background: "#eceff1",
                                    marginLeft: 12,
                                    marginRight: 12,
                                  }}
                                />
                              </>
                            )}

                            {showCommentAction && (
                              <>
                                <button
                                  type="button"
                                  className={styles.detail_comment_button}
                                  aria-label="Comment"
                                  onClick={() => handleCommentClick()}
                                >
                                  {t("comment", { ns: "layout_components" })}
                                </button>
                              </>
                            )}
                          </Flex>

                          <Flex direction="row" alignItems="center" gap="10px">
                            {showDeleteAction && <RemoveMessageButton />}
                            {showMoveAction && (
                              <ActionButton
                                UNSAFE_className={styles.no_border}
                                onPress={openMoveDialog}
                                aria-label={t("move", {
                                  ns: "layout_components",
                                  defaultValue: "Move",
                                })}
                              >
                                <i className="bi bi-arrow-right-circle" style={{ fontSize: "1.6em", color: 'var(--neutral-50)' }} />
                           
                              </ActionButton>
                            )}
                          </Flex>
                        </Flex>
                      </div>

                      {/* Message metadata and subject */}
                      <View
                        UNSAFE_className={styles.email_content_header_parent}
                        UNSAFE_style={{
                          borderBottom: "1px solid #eceff1",
                          padding: "16px",
                        }}
                      >
                        <Flex direction="row" gap="18px" alignItems="start">
                          <div className={styles.user_img} aria-hidden>
                            <img
                              id="img_MessgeList"
                              className={styles.avatar_desktop_light}
                              alt=""
                              src="../../../../../../assets/images/avatar--desktop--light@2x.png"
                            />
                          </div>

                          <Flex direction="column" width="100%" gap="6px">
                            <Flex
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Flex
                                direction="row"
                                alignItems="center"
                                gap="8px"
                              >
                                <View UNSAFE_className={styles.user_full_name}>
                                  {getSenderName(selectedDataRow.sender)}
                                </View>
                                <View
                                  UNSAFE_style={{
                                    width: "1px",
                                    height: "12px",
                                    background: "transparent",
                                  }}
                                />
                                <Flex
                                  direction="row"
                                  alignItems="center"
                                  gap="size-50"
                                >
                                  <View UNSAFE_className={styles.user_email}>
                                    {getSenderEmail(selectedDataRow.mailFrom)}
                                  </View>
                                  <EmailSenderContactBadge
                                    messageRow={senderContactBadgeRow}
                                    mailFrom={selectedDataRow.mailFrom}
                                    sender={selectedDataRow.sender ?? ""}
                                    enabled={Boolean(selectedDataRow?.mailFrom)}
                                  />
                                </Flex>
                              </Flex>

                              <Text
                                UNSAFE_className={styles.div_date}
                                aria-hidden
                              >
                                {moment(selectedDataRow.date).format(
                                  "DD.MM.YYYY HH:mm",
                                )}
                              </Text>
                            </Flex>

                            <View
                              UNSAFE_className={styles.email_subject}
                              UNSAFE_style={{
                                fontSize: "18px",
                                fontWeight: 600,
                              }}
                            >
                              {selectedDataRow.subject}
                            </View>

                            <RecipientToField
                              recipient={selectedDataRow?.RECIPIENT}
                            />

                            <ContextPathLinks
                              message={messageContent.MESSAGE}
                              styles={styles}
                            />

                            <RecipientNotifyField
                              recipient={messageContent?.MESSAGE?.RECIPIENT}
                            />

                            {messageContent?.participantsName && (
                              <View
                                UNSAFE_style={{
                                  marginTop: "6px",
                                  color: "#6f7e85",
                                  fontSize: "13px",
                                }}
                              >
                                {t("participants", {
                                  ns: "layout_components",
                                  defaultValue: "Participants",
                                })}
                                : {messageContent.participantsName}
                              </View>
                            )}

                            <MessageAttachment
                              store={store}
                              styles={styles}
                              messageContent={messageContent}
                            />
                          </Flex>
                        </Flex>
                      </View>

                      {/* Message body */}
                      <View
                        UNSAFE_className={styles.email_content}
                        UNSAFE_style={{
                          padding: "16px",
                          overflow: "auto",
                          flex: 1,
                        }}
                      >
                        {/* <div
                          title={messageContent.MESSAGE?.PREVIEW?.contentv}
                          dangerouslySetInnerHTML={{
                            __html: oldEmailContentFix(
                              messageContent.MESSAGE?.PREVIEW?.content
                            ),
                          }}
                        /> */}

                        <iframe
                          srcDoc={oldEmailContentFix(
                            messageContent.MESSAGE?.PREVIEW?.content,
                          )}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            backgroundColor: "#fff",
                          }}
                          title="Email Content"
                          sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
                        />
                      </View>

                      {/* Footer navigation */}
                      <Flex
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        UNSAFE_style={{ padding: "12px 0" }}
                      >
                        <Flex
                          direction="row"
                          gap="32px"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {navigationIndex > 0 && (
                            <Button
                              variant="cta"
                              UNSAFE_style={{
                                background: "transparent",
                                border: "none",
                              }}
                              onPress={previousNavigation}
                              aria-label="Previous"
                            >
                              <ArrowLeft className={styles.icon_navigate} />
                              <span style={{ marginLeft: 8 }}>
                                {t("previous", { ns: "layout_components" })}
                              </span>
                            </Button>
                          )}

                          {navigationIndex < rowsData.length - 1 && (
                            <Button
                              variant="cta"
                              UNSAFE_style={{
                                background: "transparent",
                                border: "none",
                              }}
                              onPress={nextNavigation}
                              aria-label="Next"
                            >
                              <span style={{ marginRight: 8 }}>
                                {t("next", { ns: "layout_components" })}
                              </span>
                              <ArrowRight className={styles.icon_navigate} />
                            </Button>
                          )}
                        </Flex>
                      </Flex>
                    </Flex>
                  </div>
                )}
              </View>
            )}
          </div>
        ) : (
          <View
            position={"relative"}
            top={0}
            left={0}
            width={"100%"}
            height={"100%"}
            overflow={"hidden"}
            UNSAFE_className={styles.message_list_parent}
          >
            <Flex
              position={"relative"}
              top={0}
              left={0}
              direction={"column"}
              alignItems={"start"}
              justifyContent={"start"}
            >
              <View
                position={"relative"}
                top={0}
                left={0}
                width={"100%"}
                height={"64px"}
                overflow={"hidden"}
                UNSAFE_className={styles.messages_list_header_parent}
              >
                <Flex
                  direction={"row"}
                  position={"absolute"}
                  top={24}
                  height={"32px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  marginStart={12}
                  UNSAFE_className={styles.btn_new_email}
                >
                  {/* {showNewMessageDialog && <NewMessage styles={styles} store={store} messageService={messageService} dialogVisibility={showNewMessageDialog} />} */}
                  <Button
                    variant="primary"
                    UNSAFE_style={{
                      background: "transparent",
                      border: "none",
                      width: "100%",
                      fontSize: "14px",
                    }}
                    onPress={() => {
                      //setShowNewMessageDialog(true);
                      invokeNewMessageCreating(
                        selectedItem?.id,
                        "message",
                        undefined,
                        undefined,
                      );
                    }}
                  >
                    <>
                      <Plus size={18} className={styles.btn_new_email_text} />
                      <span className={styles.btn_new_email_text}>
                        {t("new_email", { ns: "layout_components" })}
                      </span>
                    </>
                  </Button>
                </Flex>
                <Flex
                  position={"absolute"}
                  top={25}
                  right={15}
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"start"}
                  gap={18}
                >
                  <ArrowClockwise
                    className={styles.message_list_header_icon}
                    onClick={reloadMessageList}
                  />
                  {/* <SearchIcon size={24} /> */}
                  <SearchButton />
                  {/* <FilterIcon UNSAFE_className={styles.message_list_header_icon} /> */}
                  <FilterButton />
                  {showMoveAction && (
                    <ActionButton
                      UNSAFE_className={styles.no_border}
                      onPress={openMoveDialog}
                      aria-label={t("move", {
                        ns: "layout_components",
                        defaultValue: "Move",
                      })}
                    >
                      <i className="bi bi-arrow-right-circle" style={{ fontSize: "1.6em", color: 'var(--neutral-50)' }} />
                    </ActionButton>
                  )}
                </Flex>
              </View>
            </Flex>
            <NoResultsMessage />
          </View>
        )}
        {isCommentChatOpen && commentChatTarget && (
          <CommentChatWindow
            isOpen={isCommentChatOpen}
            targetMessage={commentChatTarget}
            currentContextId={selectedItem?.id}
            onClose={() => {
              setIsCommentChatOpen(false);
              setCommentChatTarget(null);
            }}
          />
        )}
        <DialogContainer
          onDismiss={() => setIsResolveOptionsOpen(false)}
          isDismissable
        >
          {isResolveOptionsOpen && (
            <ResolveMessageOptionsDialog
              onClose={() => setIsResolveOptionsOpen(false)}
              visibility={{
                reply: showReplyAction,
                replyAll: showReplyAction,
                forward: showForwardAction,
                comment: showCommentAction,
                move: false,
              }}
              onReply={() => handleReplyClick("REPLY")}
              onReplyAll={() => handleReplyClick("REPLYALL")}
              onForward={() => {
                void handleForwardClick();
              }}
              onComment={() => {
                void handleCommentClick();
              }}
              onMoveToContext={() => openMoveDialog()}
            />
          )}
        </DialogContainer>
        <DialogContainer
          onDismiss={() => {
            setIsMoveDialogOpen(false);
          }}
        >
          {isMoveDialogOpen && (
            <ContextMoveDialog
              onClose={() => setIsMoveDialogOpen(false)}
              parentItem={null}
              selectedItem={selectedItem ?? null}
              showActivities={true}
              showProjects={true} 
              showFolders={true}
              selectedItemType={FPADataTypes.FOLDER}
              onMoveContextSelected={async (selectedContextItem) => {
                var sel_treeNode = '';
                if (selectedContextItem) {
                  switch(selectedContextItem.type) {
                    case FPADataTypes.FOLDER:
                      var folder = await projectService.getFolder(new GetFolderRequest(store.Server, store.SessionId, Number(selectedContextItem.id)));
                      if (folder) {
                        sel_treeNode = folder.FOLDER.messageDefault;
                      }
                      break;
                    case FPADataTypes.PROJECT:
                      var project = await projectService.getProject(new GetProjectRequest(store.Server, store.SessionId, Number(selectedContextItem.id)));
                      if (project) {
                        sel_treeNode = project.PROJECT.defaultMessageContainer ?? '';
                      }
                      break;
                    case FPADataTypes.ACTIVITY:
                      sel_treeNode = String(selectedContextItem.id) ?? '';
                      break;
                    default:
                      sel_treeNode = '';
                      break;
                  }
                }
                const moved = await handleMoveSelectedMessage(sel_treeNode);
                return {
                  moved,
                  error: moved ? "" : "move_failed",
                  itemId: Number(selectedContextItem.id) || 0,
                };
              }}
            />
          )}
        </DialogContainer>
      </>
    );
  }
}

export const MessageListComponent = LayoutComponent(MessageList);
